import React, { useState, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Bug,
  CreditCard,
  HelpCircle,
  Lightbulb,
  Lock,
  Mail,
  X,
  ArrowLeft,
  UploadCloud,
  FileText,
  Image as ImageIcon,
  XCircle,
} from "lucide-react";
import HelpChatPanel from "./HelpChatPanel";
import {
  createSupportTicket,
  getSupportUploadUrls,
} from "../../services/v2/supportTicketService";
import { getLocalStorage } from "../../helpers/common";
import { USER_INFO_KEY } from "../../constants/common";
import "./SupportTicketModal.scss";

const CATEGORIES = [
  { id: "BUG", label: "Bug Report", Icon: Bug, helper: "Something isn't working as expected" },
  { id: "BILLING", label: "Billing", Icon: CreditCard, helper: "Payments, invoices, or plan changes" },
  { id: "QUESTION", label: "Question", Icon: HelpCircle, helper: "How does something work?" },
  { id: "FEATURE_REQUEST", label: "Feature Request", Icon: Lightbulb, helper: "Suggest an improvement" },
  { id: "ACCOUNT", label: "Account Help", Icon: Lock, helper: "Login, profile, or access issues" },
  { id: "OTHER", label: "Other", Icon: Mail, helper: "Anything else" },
];

const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = "image/png,image/jpeg,image/heic,image/webp,application/pdf";

const SupportTicketModal = ({ show, onHide }) => {
  const tenantConfig = window.__TENANT_CONFIG__ || {};
  const tenantDbId = tenantConfig.tenantDbId || 1;
  const tenantSlug = tenantConfig.id || "default";
  const tenantName = tenantConfig.displayName || tenantConfig.id || "BLS";
  const userInfo = getLocalStorage(USER_INFO_KEY)?.userInfo || {};

  const [mode, setMode] = useState("chat");
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(null);
  const [firstName, setFirstName] = useState(userInfo.firstName || "");
  const [lastName, setLastName] = useState(userInfo.lastName || "");
  const [email, setEmail] = useState(userInfo.email || "");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setMode("chat");
    setStep(1);
    setCategory(null);
    setSubject("");
    setDescription("");
    setFiles([]);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onHide && onHide();
  };

  const escalateFromChat = (transcript) => {
    setMode("ticket");
    setStep(1);
    if (transcript) {
      setDescription(
        `From chat conversation:\n\n${transcript}\n\n— What I still need help with —\n`
      );
    }
  };

  const validStep1 = !!category;
  const validStep2 = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!/^\S+@\S+\.\S+$/.test(email)) return false;
    if (subject.trim().length < 3) return false;
    if (description.trim().length < 10) return false;
    return true;
  }, [firstName, lastName, email, subject, description]);

  const ingestFiles = (incoming) => {
    const merged = [...files];
    for (const f of incoming) {
      if (merged.length >= MAX_FILES) {
        toast.warning(`Max ${MAX_FILES} attachments`);
        break;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.warning(`${f.name} exceeds 10 MB`);
        continue;
      }
      merged.push(f);
    }
    setFiles(merged);
  };

  const onPickFiles = (e) => {
    ingestFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    ingestFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      let attachments = [];
      if (files.length > 0) {
        const presignedRes = await getSupportUploadUrls({
          tenantSlug,
          files: files.map((f) => ({
            filename: f.name,
            mimeType: f.type,
            sizeBytes: f.size,
          })),
        });
        const presigned = presignedRes?.result || [];
        await Promise.all(
          presigned.map((p, i) =>
            fetch(p.presignedUrl, {
              method: "PUT",
              headers: { "Content-Type": files[i].type },
              body: files[i],
            })
          )
        );
        attachments = presigned.map((p, i) => ({
          key: p.path,
          originalFilename: p.originalFilename,
          mimeType: p.mimeType,
          sizeBytes: p.sizeBytes,
        }));
      }

      await createSupportTicket({
        tenantId: tenantDbId,
        category: category.id,
        priority: category.id === "BUG" ? "HIGH" : "NORMAL",
        submitterName: `${firstName.trim()} ${lastName.trim()}`,
        submitterEmail: email.trim(),
        subject: subject.trim(),
        description: description.trim(),
        sourceUrl: window.location.href,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        attachments,
      });
      toast.success("We got your request - check your email for confirmation");
      reset();
      onHide && onHide();
    } catch (err) {
      toast.error(err?.message || "Could not submit ticket. Try again.");
      setSubmitting(false);
    }
  };

  const chatContext = {
    tenantName,
    userFirstName: userInfo.firstName,
    userType: userInfo.userType || userInfo.UserTypeId,
    currentPage:
      typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : null,
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop={submitting ? "static" : true}
      keyboard={!submitting}
      dialogClassName="support-ticket-modal"
    >
      <Modal.Body>
        <div className="support-ticket-header">
          <div>
            <h3>How can we help you?</h3>
            <div className="mode-tabs">
              <button
                type="button"
                className={`mode-tab${mode === "chat" ? " active" : ""}`}
                onClick={() => {
                  if (!submitting) {
                    setMode("chat");
                  }
                }}
              >
                Chat with assistant
              </button>
              <button
                type="button"
                className={`mode-tab${mode === "ticket" ? " active" : ""}`}
                onClick={() => {
                  if (!submitting) {
                    setMode("ticket");
                    setStep(1);
                  }
                }}
              >
                Send a ticket
              </button>
            </div>
          </div>
          <button type="button" className="close-btn" onClick={handleClose} aria-label="Close">
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        {mode === "chat" && (
          <HelpChatPanel
            context={chatContext}
            onEscalateToTicket={escalateFromChat}
          />
        )}

        {mode === "ticket" && step === 1 && (
          <div className="support-step support-step-category">
            <p className="step-helper">Pick a category so we can route your message to the right person.</p>
            <div className="category-grid">
              {CATEGORIES.map((c) => {
                const IconComp = c.Icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`category-card${category?.id === c.id ? " selected" : ""}`}
                    onClick={() => {
                      setCategory(c);
                      setStep(2);
                    }}
                  >
                    <span className="category-icon-wrap">
                      <IconComp size={22} strokeWidth={1.6} />
                    </span>
                    <span className="category-label">{c.label}</span>
                    <span className="category-helper">{c.helper}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {mode === "ticket" && step === 2 && (
          <div className="support-step support-step-details">
            <div className="step-tag-row">
              <button
                type="button"
                className="back-btn"
                onClick={() => setStep(1)}
                disabled={submitting}
                aria-label="Back"
              >
                <ArrowLeft size={16} strokeWidth={2} />
              </button>
              <div className="step-tag">
                {category.Icon && <category.Icon size={14} strokeWidth={1.8} />}
                <span>{category.label}</span>
              </div>
            </div>
            <div className="row-2">
              <div className="field">
                <label>First name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Sarah"
                />
              </div>
              <div className="field">
                <label>Last name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Mitchell"
                />
              </div>
            </div>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="field">
              <label>Subject</label>
              <input
                type="text"
                placeholder="Short summary"
                maxLength={120}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Describe what's happening</label>
              <textarea
                placeholder="What were you trying to do? What did you expect? What happened instead?"
                rows={5}
                maxLength={4000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <div className="char-counter">{description.length}/4000</div>
            </div>
            <div className="field attachments-field">
              <label>Attachments <span className="label-meta">optional · up to {MAX_FILES}</span></label>
              <label
                className={`drop-zone${dragOver ? " over" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!dragOver) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                <input
                  type="file"
                  multiple
                  accept={ACCEPTED_TYPES}
                  onChange={onPickFiles}
                />
                <UploadCloud size={22} strokeWidth={1.6} />
                <div className="drop-zone-text">
                  <strong>Drag files here</strong>
                  <span>or click to browse · PNG, JPG, HEIC, WebP, PDF · max 10 MB each</span>
                </div>
              </label>
              {files.length > 0 && (
                <ul className="file-list">
                  {files.map((f, idx) => {
                    const isImage = f.type && f.type.startsWith("image/");
                    return (
                      <li key={idx}>
                        <span className="file-icon">
                          {isImage ? (
                            <ImageIcon size={16} strokeWidth={1.8} />
                          ) : (
                            <FileText size={16} strokeWidth={1.8} />
                          )}
                        </span>
                        <span className="file-name" title={f.name}>{f.name}</span>
                        <span className="file-size">
                          {(f.size / 1024).toFixed(0)} KB
                        </span>
                        <button
                          type="button"
                          className="file-remove"
                          onClick={() => removeFile(idx)}
                          aria-label={`Remove ${f.name}`}
                        >
                          <XCircle size={16} strokeWidth={1.8} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="step-actions">
              <Button
                variant="primary"
                className="primary-cta"
                disabled={!validStep2 || submitting}
                onClick={submit}
              >
                {submitting ? "Sending..." : "Send to support"}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SupportTicketModal;
